import {FC} from 'react'
import {Document, Page, View, Text, Image, StyleSheet} from '@react-pdf/renderer'
import lpdpLogo from '../../../../src/assets/img/logo-lpdp.png'
import bgImage from '../../assets/img/bg.png'
import {capitalizeWords} from 'thesis-and-dissertations/helpers/helper'

type Props = {
  data: any
}

const PdfPage: FC<Props> = ({data}) => {
  return (
    <Document>
      {/* PAGE 1 */}
      <Page size='A4' style={styles.page}>
        <Image src={bgImage} style={styles.background} fixed />
        <View style={styles.section}>
          {/* Header */}
          <View style={styles.header}>
            <Image src={lpdpLogo} style={styles.logo} />
            <Image src={data?.foto_profil_url || ''} style={styles.profilePic} />
          </View>

          {/* Badge */}
          <View style={styles.badges}>
            {data?.jenjang_studi && <Text style={styles.badge}>{data.jenjang_studi}</Text>}
            {data?.batch_pk && <Text style={styles.badge}>{data.batch_pk}</Text>}
            {data?.tahun_lulus && <Text style={styles.badge}>{data.tahun_lulus}</Text>}
            {data?.program_beasiswa && <Text style={styles.badge}>{data.program_beasiswa}</Text>}
          </View>

          {/* Nama */}
          <View style={{marginBottom: 20}}>
            <Text style={styles.greeting}>Hello! My Name is</Text>
            <Text style={styles.fullName}>{capitalizeWords(data?.nama_lengkap || '')}</Text>
            <Text style={styles.job}>
              {data?.pekerjaan_saat_ini || ''}{' '}
              {data?.perusahaan_instansi && `(${data.perusahaan_instansi})`}
            </Text>
          </View>

          {/* Biography */}
          {data?.biography && (
            <View style={styles.bioBox}>
              <Text style={styles.subtitle}>Biography</Text>
              <Text style={styles.bioText}>{data.biography}</Text>
            </View>
          )}

          {/* Kutipan */}
          {data?.kutipan_inspiratif && (
            <View style={styles.quoteBox}>
              <Text style={styles.quote}>"{data.kutipan_inspiratif}"</Text>
              <View style={styles.quoteFooter}>
                <Image src={data?.foto_profil_url || ''} style={styles.avatar} />
                <View>
                  <Text style={styles.nameSmall}>{capitalizeWords(data?.nama_lengkap || '')}</Text>
                  <Text style={styles.alumniSmall}>LPDP Alumni {data?.tahun_lulus || ''}</Text>
                </View>
              </View>
            </View>
          )}
        </View>
      </Page>

      {/* PAGE 2 */}
      <Page size='A4' style={styles.page}>
        <Image src={bgImage} style={styles.background} fixed />
        <View style={styles.section}>
          {/* Jejak Karier */}
          {(data?.pekerjaan_saat_ini || data?.perusahaan_instansi) && (
            <View style={styles.box}>
              <Text style={styles.subtitle}>Jejak Karier & Kontribusi</Text>
              <Text style={styles.normalText}>
                {data?.pekerjaan_saat_ini}{' '}
                {data?.perusahaan_instansi && `di ${data.perusahaan_instansi}`}
              </Text>
            </View>
          )}

          {/* Perjalanan Karier */}
          {data?.deskripsi_singkat_pekerjaan && (
            <View style={styles.box}>
              <Text style={styles.subtitle}>Perjalanan Karier</Text>
              <Text style={styles.normalText}>{data.deskripsi_singkat_pekerjaan}</Text>
            </View>
          )}

          {/* Prestasi */}
          {data?.prestasi_utama && (
            <View style={styles.box}>
              <Text style={styles.subtitle}>Prestasi Utama</Text>
              {data.prestasi_utama.split(';').map((item: string, index: number) => (
                <Text key={index} style={styles.listItem}>
                  {item.trim()}
                </Text>
              ))}
            </View>
          )}

          {/* Kontribusi */}
          {data?.kontribusi_masyarakat && (
            <View style={styles.box}>
              <Text style={styles.subtitle}>Kontribusi untuk Masyarakat</Text>
              <Text style={styles.normalText}>{data.kontribusi_masyarakat}</Text>
            </View>
          )}
        </View>
      </Page>

      {/* PAGE 3 */}
      <Page size='A4' style={styles.page}>
        <Image src={bgImage} style={styles.background} fixed />
        <View style={styles.section}>
          {/* Pesan untuk Awardee */}
          {data?.pesan_untuk_calon_awardee && (
            <View style={styles.box}>
              <Text style={styles.subtitle}>Pesan untuk Calon Awardee LPDP</Text>
              <Text style={styles.quote}>{data.pesan_untuk_calon_awardee}</Text>
            </View>
          )}

          {/* Kenangan Studi */}
          {data?.kenangan_paling_berkesan_studi && (
            <View style={styles.box}>
              <Text style={styles.subtitle}>Kenangan Paling Berkesan Selama Studi</Text>
              <Text style={styles.quote}>{data.kenangan_paling_berkesan_studi}</Text>
            </View>
          )}
        </View>
      </Page>
    </Document>
  )
}

// Styles
const styles = StyleSheet.create({
  page: {
    position: 'relative',
    padding: 40,
  },
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
  },
  section: {
    marginBottom: 20,
    padding: 12,
    // backgroundColor: '#ffffffcc', // transparan sedikit
    borderRadius: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 25,
  },
  logo: {
    width: 100,
    height: 35,
  },
  profilePic: {
    width: 120,
    height: 150,
    borderRadius: 10,
  },
  badges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 20,
  },
  badge: {
    backgroundColor: '#F26B01',
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
    padding: 6,
    borderRadius: 12,
    marginRight: 6,
    marginBottom: 6,
  },
  greeting: {
    fontSize: 12,
    color: '#444',
  },
  fullName: {
    fontSize: 24,
    fontWeight: 'bold',
    marginTop: 8,
    color: '#1f2937',
  },
  job: {
    fontSize: 14,
    marginTop: 6,
    color: '#555',
  },
  bioBox: {
    backgroundColor: '#fdf9ea',
    padding: 15,
    borderRadius: 10,
    marginBottom: 20,
  },
  subtitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#111',
  },
  bioText: {
    fontSize: 12,
    color: '#444',
  },
  normalText: {
    fontSize: 12,
    marginBottom: 6,
    color: '#333',
  },
  box: {
    backgroundColor: '#f9fafb',
    padding: 15,
    borderRadius: 10,
    marginBottom: 15,
  },
  listItem: {
    fontSize: 12,
    marginBottom: 4,
    color: '#333',
  },
  quoteBox: {
    backgroundColor: '#f9fafb',
    padding: 20,
    borderRadius: 10,
  },
  quote: {
    fontSize: 12,
    fontStyle: 'italic',
    marginBottom: 10,
    color: '#333',
  },
  quoteFooter: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 10,
  },
  nameSmall: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  alumniSmall: {
    fontSize: 10,
    color: '#777',
  },
})

export default PdfPage
